/**
 * Dashboard `?debug=1` panel — a thin wrapper over the shared
 * `@authe/ui-core/debug` `<DebugPanel>`. It injects the dashboard-specific rows
 * (env, the identity/tenancy claims, the resolved Auth0 target) and leans on the
 * shared panel for every generic affordance: copyable ids, the scopes list, the
 * raw-JWT reveal, sign-out (incl. federated), clear-cache and the feature-flag
 * toggles — so that diagnostics logic is maintained once across the fleet.
 *
 * Mounted inside the authenticated shell (below `<RequireSession>` +
 * `SessionScopedProviders`), so `useSession`/`useFeatureFlags` are available.
 */
import * as React from "react";
import { DebugPanel as SharedDebugPanel } from "@authe/ui-core/debug";
import { useSession } from "@authe/ui-core/session";
import { appEnv, auth0Config } from "@/config/env";
import { transportFlagDefaults } from "@/config";

export function DebugPanel(): React.ReactElement | null {
  const { session, logout, getAccessToken } = useSession();
  const claims = session?.claims;
  const qaColour =
    (claims?.qa_config as { environment?: string } | null | undefined)?.environment ?? "green";

  return (
    <SharedDebugPanel
      title="Dashboard debug"
      rows={[
        { label: "env", value: appEnv.env },
        { label: "org", value: claims?.org_id ?? "—", copyable: true },
        { label: "tenant", value: claims?.tenant_id ?? "—", copyable: true },
        { label: "user id", value: claims?.user_id ?? session?.user.id ?? "—", copyable: true },
        { label: "email", value: session?.user.email ?? "—", copyable: true },
        { label: "continent", value: claims?.continent ?? "—" },
        { label: "qa colour", value: qaColour },
        { label: "application", value: claims?.application ?? "—" },
        { label: "client id", value: auth0Config.clientId, copyable: true },
        { label: "audience", value: auth0Config.audience ?? "—", copyable: true },
        { label: "domain", value: auth0Config.domain },
        { label: "session", value: session ? "authenticated" : "—" },
      ]}
      scopes={claims?.scopes ?? []}
      getAccessToken={getAccessToken}
      // The user-facing sign-out (ProfileMenu) deliberately skips `federated`;
      // this is a dev-gated (`?debug=1`) tool, so it also offers a federated
      // sign-out to fully reset the upstream IdP session when testing accounts.
      onSignOut={({ federated }) =>
        void logout({ returnTo: `${window.location.origin}/login?loggedout=1`, federated })
      }
      federatedSignOut
      flagNames={Object.keys(transportFlagDefaults())}
    />
  );
}
